import type {
  CreatorsInListDTO,
  SavedListOutreachChannelDTO,
  SavedListOutreachStatusDTO,
  SavedListOutreachStatusItemDTO,
} from '@/entities/saved-creators-list';
import { getPlatformLogo, getPlatformName } from '@/entities/public-filter';
import { ProfileImage } from '@/shared/components/profile-image';
import { Badge } from '@/shared/components/shadcn/badge';
import { Button } from '@/shared/components/shadcn/button';
import { Checkbox } from '@/shared/components/shadcn/checkbox';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/shadcn/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/shadcn/table';
import { TooltipFlat } from '@/shared/components/shadcn/tooltip/tooltip-flat';
import { TableSkeleton } from '@/shared/components/table-skeleton/table-skeleton';
import { formatCompactPercent, formatNumber } from '@/shared/utils/format-number';
import { Copy, ExternalLink, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  SAVED_LIST_OUTREACH_CHANNEL_LABELS,
  SAVED_LIST_OUTREACH_STATUS_LABELS,
  SAVED_LIST_OUTREACH_STATUS_OPTIONS,
  getSavedListOutreachStatus,
  isSavedListCreatorConfirmed,
} from '../../helpers/saved-list-outreach-status';

const GRID_COLUMNS =
  'minmax(48px,0.2fr) minmax(220px,1.2fr) minmax(180px,1fr) minmax(112px,0.6fr) minmax(96px,0.55fr) minmax(156px,0.8fr) minmax(156px,0.8fr) minmax(104px,0.55fr)';

type SavedListCreatorsTableProps = {
  creators: CreatorsInListDTO[];
  disabled?: boolean;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  isLoading?: boolean;
  onRemoveCreator: (creatorId: string) => void;
  onSelectedCreatorIdsChange: (creatorIds: string[]) => void;
  onStatusChange: (params: {
    channel: SavedListOutreachChannelDTO;
    creatorId: string;
    status: SavedListOutreachStatusDTO;
  }) => void;
  outreachStatuses: SavedListOutreachStatusItemDTO[];
  selectedCreatorIds: string[];
};

export function SavedListCreatorsTable({
  creators,
  disabled = false,
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
  isLoading = false,
  onRemoveCreator,
  onSelectedCreatorIdsChange,
  onStatusChange,
  outreachStatuses,
  selectedCreatorIds,
}: SavedListCreatorsTableProps) {
  const creatorIds = creators.map((creator) => creator.id);
  const selectedIdSet = new Set(selectedCreatorIds);
  const selectedVisibleCount = creatorIds.filter((creatorId) =>
    selectedIdSet.has(creatorId),
  ).length;
  const allVisibleSelected = creators.length > 0 && selectedVisibleCount === creators.length;
  const partiallySelected = selectedVisibleCount > 0 && !allVisibleSelected;

  const handleToggleAllVisible = () => {
    if (allVisibleSelected) {
      onSelectedCreatorIdsChange(
        selectedCreatorIds.filter((creatorId) => !creatorIds.includes(creatorId)),
      );
      return;
    }

    onSelectedCreatorIdsChange(Array.from(new Set([...selectedCreatorIds, ...creatorIds])));
  };

  const handleToggleCreator = (creatorId: string) => {
    if (selectedIdSet.has(creatorId)) {
      onSelectedCreatorIdsChange(selectedCreatorIds.filter((id) => id !== creatorId));
      return;
    }

    onSelectedCreatorIdsChange([...selectedCreatorIds, creatorId]);
  };

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-xl border">
        <TableSkeleton
          rows={8}
          rowHeight="h-[72px]"
          columns={[
            { className: 'w-8' },
            { className: 'flex-1' },
            { className: 'flex-1' },
            { className: 'w-20' },
            { className: 'w-16' },
            { className: 'w-28' },
            { className: 'w-28' },
            { className: 'w-16' },
          ]}
        />
      </div>
    );
  }

  if (creators.length === 0) {
    return (
      <div className="bg-muted border-border rounded-xl border border-dashed px-6 py-12 text-center">
        <p className="text-foreground text-base font-medium">No saved creators yet</p>
        <p className="text-muted-foreground mt-2 text-sm leading-6">
          Creators you save will appear here for outreach and campaign planning.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border">
      <Table>
        <TableHeader>
          <TableRow gridTemplateColumns={GRID_COLUMNS}>
            <TableHead>
              <Checkbox
                aria-label="Select all visible creators"
                checked={partiallySelected ? 'indeterminate' : allVisibleSelected}
                onCheckedChange={() => handleToggleAllVisible()}
              />
            </TableHead>
            <TableHead>Creator</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Followers</TableHead>
            <TableHead>Avg ER</TableHead>
            <TableHead>Email status</TableHead>
            <TableHead>TikTok DM status</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {creators.map((creator) => (
            <SavedListCreatorTableRow
              key={creator.id}
              creator={creator}
              disabled={disabled}
              isSelected={selectedIdSet.has(creator.id)}
              outreachStatuses={outreachStatuses}
              onRemoveCreator={onRemoveCreator}
              onSelectCreator={handleToggleCreator}
              onStatusChange={onStatusChange}
            />
          ))}
        </TableBody>
      </Table>

      {hasNextPage || isFetchingNextPage ? (
        <div className="border-border flex justify-center border-t px-4 py-3">
          <Button
            variant="outline"
            size="sm"
            disabled={!hasNextPage || isFetchingNextPage}
            onClick={() => {
              void fetchNextPage?.();
            }}
          >
            {isFetchingNextPage ? 'Loading...' : 'Load more'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

type SavedListCreatorTableRowProps = {
  creator: CreatorsInListDTO;
  disabled: boolean;
  isSelected: boolean;
  onRemoveCreator: (creatorId: string) => void;
  onSelectCreator: (creatorId: string) => void;
  onStatusChange: (params: {
    channel: SavedListOutreachChannelDTO;
    creatorId: string;
    status: SavedListOutreachStatusDTO;
  }) => void;
  outreachStatuses: SavedListOutreachStatusItemDTO[];
};

function SavedListCreatorTableRow({
  creator,
  disabled,
  isSelected,
  onRemoveCreator,
  onSelectCreator,
  onStatusChange,
  outreachStatuses,
}: SavedListCreatorTableRowProps) {
  const PlatformLogo = getPlatformLogo(creator.platform);
  const isConfirmed = isSavedListCreatorConfirmed(outreachStatuses, creator.id);

  return (
    <TableRow
      data-state={isSelected ? 'selected' : undefined}
      gridTemplateColumns={GRID_COLUMNS}
      className={
        isConfirmed
          ? 'bg-emerald-50/60 hover:bg-emerald-50 data-[state=selected]:bg-emerald-100/70'
          : undefined
      }
    >
      <TableCell>
        <Checkbox
          aria-label={`Select ${creator.nickname}`}
          checked={isSelected}
          onCheckedChange={() => onSelectCreator(creator.id)}
        />
      </TableCell>
      <TableCell className="items-start">
        <div className="flex min-w-0 items-center gap-3">
          <ProfileImage
            className="size-10 rounded-full object-cover"
            src={creator.profile_image_url}
            name={creator.username}
          />
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-1.5">
              <span className="text-foreground min-w-0 truncate font-semibold">
                {creator.nickname}
              </span>
              {PlatformLogo ? (
                <span
                  className="text-muted-foreground shrink-0"
                  title={getPlatformName(creator.platform)}
                  aria-label={getPlatformName(creator.platform)}
                >
                  <PlatformLogo className="size-4" />
                </span>
              ) : null}
              {isConfirmed ? (
                <Badge variant="secondary" className="shrink-0">
                  Campaign-ready
                </Badge>
              ) : null}
            </div>
            <div className="text-muted-foreground flex min-w-0 items-center gap-1 text-sm">
              <span className="min-w-0 truncate">@{creator.username}</span>
              <TooltipFlat asChild side="top" content="Open profile">
                <a
                  href={creator.profile_url}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-foreground inline-flex shrink-0 transition-colors"
                  aria-label={`Open ${creator.nickname} profile`}
                >
                  <ExternalLink className="size-3.5" />
                </a>
              </TooltipFlat>
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell>
        {creator.email ? (
          <div className="text-muted-foreground flex min-w-0 items-center justify-between gap-2 text-sm">
            <span className="min-w-0 truncate" title={creator.email}>
              {creator.email}
            </span>
            <TooltipFlat asChild side="top" content="Copy email">
              <Button
                variant="ghost"
                size="icon"
                className="size-8 p-0"
                onClick={() => {
                  void handleCopyEmail(creator.email ?? '');
                }}
                aria-label={`Copy email for ${creator.nickname}`}
              >
                <Copy className="size-3.5" />
              </Button>
            </TooltipFlat>
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">No email yet</span>
        )}
      </TableCell>
      <TableCell>
        <span className="text-foreground text-sm font-medium">
          {formatNumber(creator.followers)}
        </span>
      </TableCell>
      <TableCell>
        <span className="text-foreground text-sm font-medium">
          {formatCompactPercent(creator.recent_avg_er)}
        </span>
      </TableCell>
      <TableCell>
        <SavedListOutreachStatusSelect
          channel="email"
          creatorId={creator.id}
          disabled={disabled}
          statuses={outreachStatuses}
          onStatusChange={onStatusChange}
        />
      </TableCell>
      <TableCell>
        <SavedListOutreachStatusSelect
          channel="tiktok_dm"
          creatorId={creator.id}
          disabled={disabled}
          statuses={outreachStatuses}
          onStatusChange={onStatusChange}
        />
      </TableCell>
      <TableCell>
        <TooltipFlat asChild side="top" content="Remove from saved list">
          <Button
            variant="ghost"
            size="icon"
            className="size-8 p-0"
            onClick={() => onRemoveCreator(creator.id)}
            aria-label={`Remove ${creator.nickname} from saved list`}
          >
            <Trash2 className="size-4" />
          </Button>
        </TooltipFlat>
      </TableCell>
    </TableRow>
  );
}

function SavedListOutreachStatusSelect({
  channel,
  creatorId,
  disabled,
  onStatusChange,
  statuses,
}: {
  channel: SavedListOutreachChannelDTO;
  creatorId: string;
  disabled: boolean;
  onStatusChange: (params: {
    channel: SavedListOutreachChannelDTO;
    creatorId: string;
    status: SavedListOutreachStatusDTO;
  }) => void;
  statuses: SavedListOutreachStatusItemDTO[];
}) {
  const status = getSavedListOutreachStatus(statuses, creatorId, channel);

  return (
    <Select
      value={status}
      disabled={disabled}
      onValueChange={(value) => {
        onStatusChange({
          channel,
          creatorId,
          status: value as SavedListOutreachStatusDTO,
        });
      }}
    >
      <SelectTrigger
        size="sm"
        className="w-full"
        aria-label={`${SAVED_LIST_OUTREACH_CHANNEL_LABELS[channel]} status`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {SAVED_LIST_OUTREACH_STATUS_OPTIONS.map((option) => (
            <SelectItem key={option} value={option}>
              {SAVED_LIST_OUTREACH_STATUS_LABELS[option]}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

async function handleCopyEmail(email: string) {
  try {
    await navigator.clipboard.writeText(email);
    toast.success('Email copied to clipboard');
  } catch {
    toast.error('Failed to copy email');
  }
}
